import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { flattenQuery } from 'helpers/flattenQuery';
import type { FolderPath } from 'helpers/folderPaths';
import { FilterConfig } from 'hooks/useListQuery';

export interface TableItem {
  table_id: number;
  table_name: string;
  table_columns: object[]; // Columns structure as JSON
  table_privileges: object; // Privileges structure as JSON
  zone_id: number;
  zone_name?: string;
  // Folder support (nap-backend #1076). Zone-scoped. `folder_id` is always
  // sent (null clears); `folder_path` is response-only display data.
  folder_id: number | null;
  folder_path?: FolderPath | null;
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface TablesState {
  items: TableItem[];
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  config: { header_key: string; label: string; header_type: string }[];
  filtersConfig: FilterConfig[];
  loading: {
    getTablesPaginated: boolean;
  };
  currentTable: TableItem | null;
  currentTableStatus: string | null;
}

const initialState: TablesState = {
  items: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    getTablesPaginated: false,
  },
  currentTable: null,
  currentTableStatus: null,
};

export const getTablesPaginated = createAsyncThunk('tables/getTablesPaginated', async (params: Record<string, any>) => {
  const res = await api.get('/tables', { params: flattenQuery(params) });
  return res.data;
});

// Fetch details of a single table
export const fetchTable = createAsyncThunk('tables/fetchTable', async ({ zone_name, table_name }: { zone_name: string; table_name: string }) => {
  const response = await api.get(`/tables/${zone_name}/${table_name}`);
  return response.data;
});

// Create a new table
export const createTable = createAsyncThunk<
  TableItem,
  Omit<TableItem, 'table_id' | 'zone_id'> & { zone_name: string },
  { rejectValue: { error?: string; msg?: string } }
>('tables/createTable', async (table, { rejectWithValue }) => {
  try {
    const response = await api.post('/tables', table);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { msg: err.message });
  }
});

// Update an existing table
export const updateTable = createAsyncThunk<
  TableItem,
  TableItem & {
    zone_name: string;
    original_zone_name: string;
    original_table_name: string;
  },
  { rejectValue: { error?: string; msg?: string } }
>('tables/updateTable', async (table, { rejectWithValue }) => {
  try {
    const { original_zone_name, original_table_name, ...body } = table;
    const response = await api.put(`/tables/${original_zone_name}/${original_table_name}`, body);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { msg: err.message });
  }
});

// Delete a table
export const deleteTable = createAsyncThunk('tables/deleteTable', async ({ zone_name, table_name }: { zone_name: string; table_name: string }) => {
  await api.delete(`/tables/${zone_name}/${table_name}`);
  return { zone_name, table_name };
});

const tablesSlice = createSlice({
  name: 'tables',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ================== getTablesPaginated ==================
      .addCase(getTablesPaginated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getTablesPaginated = true;
      })
      .addCase(getTablesPaginated.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { tables, config, filtersConfig, pagination } = action.payload;

        state.items = tables || [];
        state.config = config || [];
        state.filtersConfig = filtersConfig || [];

        state.pagination.totalItems = pagination?.total_records ?? 0;
        state.pagination.totalPages = pagination?.total_pages ?? 0;
        state.pagination.pageSize = pagination?.limit ?? state.pagination.pageSize;

        state.pagination.currentPage = (pagination?.current_page ?? 1) > (pagination?.total_pages ?? 0) ? 1 : (pagination?.current_page ?? 1);

        state.loading.getTablesPaginated = false;
      })
      .addCase(getTablesPaginated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch tables';
        state.loading.getTablesPaginated = false;
      })

      // ================== fetchTable ==================
      .addCase(fetchTable.pending, (state) => {
        state.currentTableStatus = 'loading';
      })
      .addCase(fetchTable.fulfilled, (state, action) => {
        state.currentTable = action.payload;
        state.currentTableStatus = 'succeeded';
      })
      .addCase(fetchTable.rejected, (state) => {
        state.currentTableStatus = 'failed';
      })

      // ================== createTable ==================
      .addCase(createTable.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })

      // ================== updateTable ==================
      .addCase(updateTable.fulfilled, (state, action) => {
        const index = state.items.findIndex((t) => t.table_id === action.payload.table_id);
        if (index !== -1) state.items[index] = action.payload;
      })

      // ================== deleteTable ==================
      .addCase(deleteTable.fulfilled, (state, action) => {
        const { zone_name, table_name } = action.payload;
        state.items = state.items.filter((t) => !(t.table_name === table_name && t.zone_name === zone_name));
      });
  },
});

export default tablesSlice.reducer;
